<template>
    <b-modal size="md"
             centered
             hide-footer
             v-model="value"
             v-on:hide="hideModal"
             v-on:cancel="hideModal"
             v-on:close="hideModal">
        <template v-slot:modal-header>
            <strong>新增{{ stageInComponent ? stageInComponent.name : "" }}事項</strong>
        </template>

        <b-form v-on:submit.prevent="submit">
            <b-form-group label-for="title">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'heading']"></font-awesome-icon> 標題
                </template>
                <b-form-input id="title"
                              class="bg-light"
                              required
                              v-model="title">
                </b-form-input>
            </b-form-group>

            <b-form-group label-for="description">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'info-square']"></font-awesome-icon> 描述
                </template>
                <b-form-textarea id="description"
                                 class="bg-light"
                                 v-model="description">
                </b-form-textarea>
            </b-form-group>

            <b-form-group label-for="service">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'store-alt']"></font-awesome-icon> 服務
                </template>
                <v-select id="service"
                          class="bg-light"
                          label="name"
                          v-bind:options="services"
                          v-model="service">
                    <template v-slot:search="{ attributes, events }">
                        <input class="vs__search"
                               v-bind:required="!service"
                               v-bind="attributes"
                               v-on="events">
                    </template>
                    <template v-slot:option="{ name, category, photo }">
                        <div class="d-flex align-items-center">
                            <div class="mr-2">
                                <img class="img-preview"
                                     v-bind:alt="name"
                                     v-bind:src="`${ $config.apiUrl }/${ $config.apiVersion }/service/image/${ photo }`">
                            </div>
                            <div class="flex-grow-1">
                                <strong class="d-inline-block mb-0">
                                    <color-dot size="sm"
                                               v-bind:color="category.color">
                                    </color-dot>
                                    <small>{{ category.name }}</small>
                                </strong>
                                <h6 class="font-weight-bolder mb-auto">
                                    {{ name }}
                                </h6>
                            </div>
                        </div>
                    </template>
                    <template v-slot:selected-option="{ name, category, photo }">
                        <div class="d-flex align-items-center">
                            <div class="mr-2">
                                <img class="img-preview"
                                     v-bind:alt="name"
                                     v-bind:src="`${ $config.apiUrl }/${ $config.apiVersion }/service/image/${ photo }`">
                            </div>
                            <div class="flex-grow-1">
                                <strong class="d-inline-block mb-0 text-primary">
                                    <color-dot size="sm"
                                               v-bind:color="category.color">
                                    </color-dot>
                                    <small>{{ category.name }}</small>
                                </strong>
                                <h6 class="font-weight-bolder mb-auto">
                                    {{ name }}
                                </h6>
                            </div>
                        </div>
                    </template>
                </v-select>
            </b-form-group>

            <b-form-group label-for="sku"
                          v-if="service ? service.sku.length > 0 : false">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'cubes']"></font-awesome-icon> SKU選擇
                </template>
                <b-form-select id="sku"
                               required
                               v-model="sku">
                    <b-form-select-option v-for="{ _id, name } in service.sku"
                                          v-bind:key="_id"
                                          v-bind:value="_id">
                        {{ name }}
                    </b-form-select-option>
                </b-form-select>
            </b-form-group>

            <b-form-group label-for="sites">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'thumbtack']"></font-awesome-icon> 地盤
                </template>
                <v-select id="sites"
                          class="bg-light"
                          label="siteName"
                          v-bind:options="sites"
                          v-model="site">
                    <template v-slot:search="{ attributes, events }">
                        <input class="vs__search"
                               v-bind:required="!site"
                               v-bind="attributes"
                               v-on="events">
                    </template>
                    <template v-slot:option="{ siteName, address, }">
                        <div class="-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="font-weight-bold mb-1">
                                    {{ siteName }}
                                </h6>
                                <p class="mb-0">
                                    <small>{{ address }}</small>
                                </p>
                            </div>
                        </div>
                    </template>
                    <template v-slot:selected-option="{ siteName, address, }">
                        <div class="-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="font-weight-bold mb-1">
                                    {{ siteName }}
                                </h6>
                                <p class="mb-0">
                                    <small>{{ address }}</small>
                                </p>
                            </div>
                        </div>
                    </template>
                </v-select>
            </b-form-group>

            <b-form-group label-for="stage">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'hourglass-half']"></font-awesome-icon> 事項步驟
                </template>
                <b-form-select id="stage"
                               class="bg-light"
                               required
                               v-model="stageSelected">
                    <b-form-select-option v-for="{ _id, name } in taskStages"
                                          v-bind:key="_id"
                                          v-bind:value="_id">
                        {{ name }}
                    </b-form-select-option>
                </b-form-select>
            </b-form-group>

            <b-form-group label-for="provider"
                          v-if="['vending-admin'].includes(userRole)">
                <template v-slot:label>
                    <font-awesome-icon v-bind:icon="['fad', 'user']"></font-awesome-icon> 事項提出人
                </template>
                <v-select id="provider"
                          class="bg-light"
                          label="name"
                          v-bind:options="users"
                          v-model="ownerInComponent">
                    <template v-slot:search="{ attributes, events }">
                        <input class="vs__search"
                               v-bind:required="!ownerInComponent"
                               v-bind="attributes"
                               v-on="events">
                    </template>
                    <template v-slot:option="{ name, email, }">
                        <div class="d-flex align-items-center">
                            <div class="mr-2">
                                <b-avatar></b-avatar>
                            </div>
                            <div class="flex-grow-1">
                                <h6 class="font-weight-bold mb-0">
                                    {{ name }}
                                </h6>
                                <p class="mb-1">
                                    {{ email }}
                                </p>
                            </div>
                        </div>
                    </template>
                    <template v-slot:selected-option="{ name, email, }">
                        <div class="d-flex align-items-center">
                            <div class="mr-2">
                                <b-avatar></b-avatar>
                            </div>
                            <div class="flex-grow-1">
                                <h6 class="font-weight-bold mb-0">
                                    {{ name }}
                                </h6>
                                <p class="mb-1">
                                    {{ email }}
                                </p>
                            </div>
                        </div>
                    </template>
                </v-select>
            </b-form-group>

            <div class="text-center mt-3">
                <button class="btn font-weight-bolder px-4"
                        type="submit"
                        v-bind:class="{ 'btn-success': !isEditing, 'btn-warning': isEditing, 'btn-loading': isLoading }">
                    {{ isEditing? "✏️ 編輯" : "🆕 新增" }}
                </button>
            </div>
        </b-form>
    </b-modal>
</template>

<script>
import {
    BModal,
    BAvatar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
} from "bootstrap-vue";
import vSelect from "vue-select";
import ColorDot from "../ColorDot";

export default {
    name: "ModalTask",
    components: {
        ColorDot,
        "v-select": vSelect,
        BModal,
        BAvatar,
        BForm,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BFormSelect,
        BFormSelectOption,
    },
    props: {
        "value": {
            type: Boolean,
            default: false,
        },

        "isEditing": {
            type: Boolean,
            default: false,
        },

        "stage": {
            type: String,
        },
    },
    data () {
        return {
            isLoading: false,

            title: "",
            description: "",
            service: null,
            sku: null,
            site: null,
            stageSelected: "",
            owner: null,
        };
    },
    computed: {
        user () {
            return this.$store.state.user.user;
        },

        ownerInComponent: {
            get () {
                if (this.owner) {
                    return this.owner;
                }
                return this.user;
            },
            set (val) {
                this.owner = val;
            },
        },

        userRole () {
            return this.user.role;
        },

        users () {
            return this.$store.getters["users/all"];
        },

        services () {
            return this.$store.state.data.services;
        },

        sites () {
            return this.$store.state.data.sites;
        },

        taskStages () {
            return this.$store.state.tasks.stages;
        },

        stageInComponent () {
            const target = this.taskStages.find(
                ({ _id }) => _id === this.stageSelected
            );
            if (target) {
                return target;
            }
            return null;
        },
    },
    methods: {
        hideModal () {
            this.$emit("input", false);
        },

        getSiteLabel (item) {
            if (item) {
                return item.siteName;
            }
            return "";
        },

        getFilteredSites (text) {
            this.filteredSites = this.sites
                .filter(
                    ({ siteName, address, }) => {
                        return `${ siteName }${ address }`
                            .trim()
                            .toString()
                            .toLowerCase()
                            .indexOf(text.toLowerCase()) >= 0;
                    }
                );
        },

        getUserLabel (item) {
            if (item) {
                return item.name;
            }
            return "";
        },

        getFilterUsers (text) {
            this.filteredUsers = this.users
                .filter(
                    ({ name, phone, email }) => {
                        return `${ name }${ phone }${ email }`
                            .trim()
                            .toString()
                            .toLowerCase()
                            .indexOf(text.toLowerCase()) >= 0;
                    }
                );
        },

        submit () {
            if (this.isEditing) {
                this.editTask();
                return;
            }
            this.addTask();
        },

        async addTask () {
            this.isLoading = true;
            try {
                await this.$store
                    .dispatch(
                        "tasks/addTask",
                        {
                            title: this.title,
                            description: this.description,
                            stage: this.stageSelected,
                            site: this.site._id,
                            service: this.service._id,
                            sku: this.sku,
                            owner: this.ownerInComponent._id,
                        }
                    );
            } catch (e) {
                console.error(e);
                this.isLoading = false;
                return;
            }
            this.isLoading = false;
            this.reset();
            this.hideModal();
        },

        async editTask () {

        },

        reset () {
            this.title = "";
            this.description = "";

            this.service = null;
            this.sku = null;
            this.site = null;
            this.owner = null;
        },
    },
    mounted () {
        if (this.stage) {
            this.stageSelected = this.stage;
        }
    },
    watch: {
        "stage" (newVal) {
            if (newVal) {
                this.stageSelected = newVal;
            }
        },

        "service" () {
            this.sku = null;
        },
    },
}
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
.img-preview {
    width: 100px;
    height: 100px;
    object-fit: contain;
}
</style>
